/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import { Col, Container, Row, Stack } from "react-bootstrap";
import { IHeaderProps } from "./Header.types";
import HeaderStyles from "./Header.styles";
import AppMedia from "../../theme/utils/AppMedia";
import DESKTOP_LOGO from "../logo/DesktopLogo.component";
import HomeLinkIcon from "../../theme/assets/CSSO/external-link-alt-solid.svg";
import LogOutLinkIcon from "../../theme/assets/CSSO/sign-out-alt-solid.svg";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { useOktaAuth } from "@okta/okta-react";
import { OktaAuth } from "@okta/okta-auth-js";
import { useParams } from "react-router-dom";
import PageUtils from "../page/Page.utils";
import { faUser, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

const { Media } = AppMedia;

const onLogoutClicked = async (oktaAuth: OktaAuth) => {
  await oktaAuth.signOut({
    revokeAccessToken: true,
    revokeRefreshToken: true,
  });
};

const HEADER: React.FC<IHeaderProps> = (props: IHeaderProps) => {
  const sendDataToGTM = useGTMDispatch();
  const { oktaAuth, authState } = useOktaAuth();
  const { projectId }: { projectId: string } = useParams();
  const isLoggedIn = authState?.isAuthenticated ?? false;

  const [isDetailsPage, setIsDetailsPage] = useState<boolean>(false);
  useEffect(() => {
    // istanbul ignore next
    if (projectId) {
      setIsDetailsPage(true);
    } else {
      setIsDetailsPage(false);
    }
  }, [projectId]);

  let bpMessageTemplate = "";

  // istanbul ignore next
  if (isLoggedIn) {
    let bpIdentity: string | undefined = authState?.idToken?.claims?.name;
    if (bpIdentity === undefined || bpIdentity.length == 0) {
      bpIdentity = authState?.idToken?.claims?.preferred_username;
    }

    bpMessageTemplate = `Signed in as ${bpIdentity ?? ""}`;
  }

  return (
    <>
      <HeaderStyles.HeaderContainer
        className="header-container"
        data-testid="header"
      >
        <Container>
          <Row>
            <Col xs={6} md={2} className="logo-column">
              <DESKTOP_LOGO color="#fff" width={125} />
            </Col>
            <Col xs={6} md={10} className="nav-column justify-content-end">
              <Stack gap={2} direction="horizontal">
                <Media greaterThanOrEqual="tablet">
                  <HeaderStyles.NavLinkButton
                    className="nav-link-button"
                    href="http://www.consumersenergy.com"
                    onClick={
                      /* istanbul ignore next */ () => {
                        sendDataToGTM({
                          event: "eventTracking",
                          category: "Construction",
                          action: "Click - Header",
                          label: `CE logo - ${PageUtils.getGTMPageLabel(
                            isDetailsPage
                          )}`,
                        });
                      }
                    }
                  >
                    <HeaderStyles.HomeLinkIcon
                      className="home-link-icon"
                      src={HomeLinkIcon}
                    />
                    Consumers Energy Home
                  </HeaderStyles.NavLinkButton>
                </Media>

                {/* Links are stacked on mobile */}
                <Media lessThan="tablet">
                  <HeaderStyles.NavLinkButton
                    className="nav-link-button mobile"
                    href="http://www.consumersenergy.com"
                    onClick={
                      /* istanbul ignore next */ () => {
                        sendDataToGTM({
                          event: "eventTracking",
                          category: "Construction",
                          action: "Click - Header",
                          label: `CE Home icon - ${PageUtils.getGTMPageLabel(
                            isDetailsPage
                          )}`,
                        });
                      }
                    }
                  >
                    <Stack>
                      <HeaderStyles.HomeLinkIcon
                        className="home-link-icon"
                        src={HomeLinkIcon}
                      />
                      <span>CE Home</span>
                    </Stack>
                  </HeaderStyles.NavLinkButton>
                </Media>

                {isLoggedIn && (
                  <>
                    <Media greaterThanOrEqual="tablet">
                      <HeaderStyles.NavLinkButton
                        className="nav-link-button"
                        onClick={() => {
                          onLogoutClicked(oktaAuth);
                          sendDataToGTM({
                            event: "eventTracking",
                            category: "Construction",
                            action: "Click - Header",
                            label: `Log Out - ${PageUtils.getGTMPageLabel(
                              isDetailsPage
                            )}`,
                          });
                        }}
                        data-testid="header-logout-button"
                      >
                        <HeaderStyles.LogOutLinkIcon
                          className="logout-link-icon"
                          src={LogOutLinkIcon}
                        />
                        Log Out
                      </HeaderStyles.NavLinkButton>
                    </Media>

                    {/* Links are stacked on mobile */}
                    <Media lessThan="tablet">
                      <HeaderStyles.NavLinkButton
                        className="nav-link-button mobile"
                        onClick={
                          /* istanbul ignore next */ () => {
                            onLogoutClicked(oktaAuth);
                            sendDataToGTM({
                              event: "eventTracking",
                              category: "Construction",
                              action: "Click - Header",
                              label: `Log Out - ${PageUtils.getGTMPageLabel(
                                isDetailsPage
                              )}`,
                            });
                          }
                        }
                      >
                        <Stack>
                          <HeaderStyles.LogOutLinkIcon
                            className="logout-link-icon"
                            src={LogOutLinkIcon}
                          />
                          <span>Log Out</span>
                        </Stack>
                      </HeaderStyles.NavLinkButton>
                    </Media>
                  </>
                )}
              </Stack>
            </Col>
          </Row>
        </Container>
      </HeaderStyles.HeaderContainer>
      <HeaderStyles.BpAlertHeader>
        <HeaderStyles.BpAlertIcon icon={faExclamationTriangle} data-testid="bp-header-alert-icon" />
        <HeaderStyles.BpAlertText>CONSUMERS ENERGY IS EXPERIENCING MATERIAL SHORTAGES, YOUR PROJECT MAY EXPERIENCE DELAYS.</HeaderStyles.BpAlertText>
      </HeaderStyles.BpAlertHeader>
      {isLoggedIn ? (
        <HeaderStyles.BpBarHeader>
          <HeaderStyles.BpUserIcon icon={faUser} data-testid="bp-header-icon" />
          <HeaderStyles.BpText>{bpMessageTemplate}</HeaderStyles.BpText>
        </HeaderStyles.BpBarHeader>
      ) : null}
    </>
  );
};

export default HEADER;
